/* i18n-ignore: interne adminpagina, alleen NL */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Eye, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { BlogEditor } from "@/components/admin/BlogEditor";
import { BlogArticle } from "@/components/site/BlogArticle";
import { emptyPost, type BlogPost } from "@/content/blog-types";
import { adminGetPost, savePost, setPostPublished } from "@/lib/blog.functions";

export const Route = createFileRoute("/_authenticated/admin/artikels/$slug")({
  staticData: { sitemap: false },
  head: () => ({ meta: [{ title: "Artikel bewerken — Gopro Projects" }, { name: "robots", content: "noindex" }] }),
  component: AdminArticleEditorPage,
});

function AdminArticleEditorPage() {
  const { slug } = Route.useParams();
  const isNew = slug === "nieuw";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [post, setPost] = useState<BlogPost>(() => emptyPost());
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["blog", "admin", "post", slug],
    queryFn: () => adminGetPost({ data: { slug } }),
    enabled: !isNew,
  });

  useEffect(() => {
    if (data) setPost(data);
  }, [data]);

  async function handleSave(publish?: boolean) {
    if (!post.title.trim() || !post.slug.trim()) {
      toast.error("Vul minstens een titel en een webadres in.");
      return;
    }
    setSaving(true);
    try {
      const saved = await savePost({ data: post });
      if (publish !== undefined && saved.published !== publish) {
        await setPostPublished({ data: { id: saved.id, published: publish } });
      }
      await queryClient.invalidateQueries({ queryKey: ["blog"] });
      toast.success(publish === true ? "Artikel staat online." : "Artikel bewaard.");
      if (isNew || saved.slug !== slug) {
        void navigate({ to: "/admin/artikels/$slug", params: { slug: saved.slug }, replace: true });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Bewaren mislukt.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/artikels">
            <ArrowLeft className="mr-1 h-4 w-4" /> Alle artikels
          </Link>
        </Button>
        <h1 className="text-xl font-extrabold tracking-tight sm:text-2xl">
          {isNew ? "Nieuw artikel" : "Artikel bewerken"}
        </h1>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPreview((v) => !v)}>
            <Eye className="mr-1 h-4 w-4" /> {preview ? "Terug naar bewerken" : "Voorbeeld"}
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={saving} onClick={() => void handleSave()}>
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />}
            Bewaren als concept
          </Button>
          <Button type="button" size="sm" disabled={saving} onClick={() => void handleSave(true)}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Publiceren
          </Button>
        </div>
      </div>

      {isLoading && !isNew && (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      )}

      {isError && (
        <Card>
          <CardContent className="p-6 text-sm text-destructive">
            Dit artikel kon niet geladen worden. Ga terug naar de lijst en probeer opnieuw.
          </CardContent>
        </Card>
      )}

      {(isNew || data) &&
        (preview ? (
          <div className="rounded-2xl border border-outline/15 bg-background">
            <BlogArticle post={post} />
          </div>
        ) : (
          <BlogEditor value={post} onChange={setPost} />
        ))}
    </div>
  );
}
